import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminCompaniesTable } from '../-components/AdminCompaniesTable';
import { adminCompaniesQueryOptions } from '../-hooks/useAdminCompanies';
import { useAdminCompaniesPage } from '../-hooks/useAdminCompaniesPage';

const companiesListSearch = z.object({
  tab: fallback(z.enum(['active', 'removed']), 'active'),
  page: fallback(z.number().int().min(1), 1),
  // The endpoint caps `count` at 500 (`AdminCompaniesListQuerySchema`), so a
  // larger value in the URL would 400 rather than return a bigger page.
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/companies/(pages)/')({
  validateSearch: companiesListSearch,
  // Off the URL rather than a fixed first page, so a deep link with a tab, sort
  // or filter prefetches the rows it will actually show.
  // `adminCompaniesQueryOptions` is what makes these agree with the component's
  // own query — both derive the key from the converted params, so one cache
  // entry serves both.
  loaderDeps: ({ search }) => ({
    tab: search.tab ?? 'active',
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminCompaniesQueryOptions(deps))
      // A failed prefetch must not blank the roster — the component's own
      // query re-runs and surfaces the error inline.
      .catch(() => undefined),
  component: ManagementCompaniesPage,
});

function ManagementCompaniesPage() {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const page = useAdminCompaniesPage();

  return (
    // The tab row stays outside `PageContainer`: the container is the table's
    // height chain (`flex-1 min-h-0 overflow-hidden`), and anything else inside
    // it eats from the height the table needs to reach its own footer.
    <>
      {/* Active and removed are two endpoints, not a filter — the SQL pins
          `is_removed` itself — so they stay two tabs, as in the old page.
          `resetSearchOnChange` drops the page, sort and filters on the way
          over: they belong to the roster being left, and a page number from a
          longer list would land the other tab past its end. */}
      <PageTabs
        tabs={[
          { key: 'active', label: t('tabs.active') },
          { key: 'removed', label: t('tabs.removed') },
        ]}
        defaultTab='active'
        resetSearchOnChange
        moreLabel={tShared('moreTabs')}
      />

      <PageContainer>
        <AdminCompaniesTable page={page} />
      </PageContainer>
    </>
  );
}
